/* ==========================================================================
   Sticky Add To Cart — vanilla JS custom element
   - Reveals only after the native product form scrolls out of view (IntersectionObserver)
   - Mirrors the selected variant from Dawn's native product form (no duplicate state)
   - Adds to cart through Shopify AJAX cart and opens Dawn's cart drawer
   - No dependencies, no jQuery
   ========================================================================== */

if (!customElements.get('sticky-atc')) {
  class StickyAtc extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
      this.visible = false;
      this.variantId = null;
      this.planId = null;

      this.selectedVariantId = null;
      this.selectedPlanId = undefined; // undefined = "not read yet", distinct from null ("no plan")
      this.unsubscribeVariantChange = null;

      this.onSubmit = this.onSubmit.bind(this);
      this.onSync = this.onSync.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.bar = this.querySelector('[data-satc-bar]');
      this.button = this.querySelector('[data-satc-submit]');
      if (!this.bar || !this.button) return;

      this.buttonLabel = this.querySelector('[data-satc-label]');
      this.errorEl = this.querySelector('[data-satc-error]');
      this.imageEl = this.querySelector('[data-satc-image]');
      this.priceEl = this.querySelector('[data-satc-price]');
      this.compareEl = this.querySelector('[data-satc-compare]');
      this.variantEl = this.querySelector('[data-satc-variant]');
      this.variantSepEl = this.querySelector('[data-satc-variant-sep]');
      this.planEl = this.querySelector('[data-satc-plan]');
      this.planSepEl = this.querySelector('[data-satc-plan-sep]');
      this.stockEl = this.querySelector('[data-satc-stock]');
      this.stockSepEl = this.querySelector('[data-satc-stock-sep]');

      this.hiddenInput = this.querySelector('[data-satc-variant-id]');
      this.variants = this.parseVariants();
      this.lowStockThreshold = parseInt(this.dataset.lowStockThreshold || '0', 10);

      this.button.addEventListener('click', this.onSubmit);

      // Initial state: handles both the default variant and direct URL loading
      // (/products/x?variant=123), before any pubsub event has fired.
      const initial = this.variantFromUrl() || this.variantFromForm();
      if (initial) {
        this.selectedVariantId = String(initial.id);
        console.log('[sticky-atc] Initial variant:', initial.id);
      }

      this.setupVisibilityObserver();
      this.setupVariantWatcher();
      this.render();
      this.initSellingPlanSync();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      // Dawn's subscribe() returns an unsubscribe function.
      if (typeof this.unsubscribeVariantChange === 'function') {
        this.unsubscribeVariantChange();
        this.unsubscribeVariantChange = null;
      }
      document.removeEventListener('change', this.onSync, true);
      document.removeEventListener('input', this.onSync, true);
      if (this.button) this.button.removeEventListener('click', this.onSubmit);
      if (this.syncTimer) clearTimeout(this.syncTimer);
      if (this.initSyncTimer) clearTimeout(this.initSyncTimer);
    }

    /*
     * Appstle's subscription widget is injected asynchronously by Appstle's
     * OWN script, after our connectedCallback has already fired and done its
     * first render(). When a merchant sets "Subscribe & Save" as the default
     * plan, the <input name="selling_plan"> radios (and the checked one)
     * simply don't exist in the DOM yet at that first render — so
     * currentPlanId() correctly reads "no plan" at that instant, and nothing
     * ever re-renders afterwards because Appstle selecting its own default
     * is a programmatic DOM change, not a user-driven 'change'/'input'
     * event. That's why the sticky bar looked like one-time-purchase until
     * a variant change forced a fresh render.
     *
     * Fix: a short, bounded, self-cancelling poll (no MutationObserver) that
     * waits for Appstle's radio group to report a :checked option, then
     * renders once more. It only READS whichever option Appstle already
     * selected — it never checks/forces a radio itself.
     */
    initSellingPlanSync() {
      const maxAttempts = 30; // ~4.5s @150ms — generous for Appstle's async widget load
      let attempts = 0;

      const poll = () => {
        attempts += 1;
        const form = this.form;
        const checkedRadio = form && form.querySelector('input[name="selling_plan"]:checked');

        if (checkedRadio || attempts >= maxAttempts) {
          this.logInitialSellingPlan();
          this.render();
          return;
        }

        this.initSyncTimer = setTimeout(poll, 150);
      };

      poll();
    }

    logInitialSellingPlan() {
      const planId = this.currentPlanId();
      console.log('[sticky-atc] Initial selling plan detected:', planId || 'none');
      console.log('[sticky-atc] Initial purchase option:', planId ? 'Subscribe' : 'One-Time');
      console.log('[sticky-atc] Sticky ATC initialized successfully');
    }

    /* ------------------------------------------------- live DOM resolution */

    /*
     * Dawn v16 replaces the <product-form> node when a variant changes, so a
     * cached reference goes stale (detached node holding the old variant id).
     * These getters always resolve against the live document instead.
     */
    get form() {
      if (this._form && this._form.isConnected) return this._form;

      const id = this.dataset.formId;
      if (id) {
        const byId = document.getElementById(id);
        if (byId && byId.isConnected) {
          this._form = byId;
          return byId;
        }
      }

      // Scope the search so a quick-add form in "related products" can't win.
      const scopes = [
        document.querySelector('product-info'),
        document.getElementById('MainProduct'),
        document.querySelector('.product:not(.card)'),
        document.querySelector('main'),
        document,
      ];

      for (let i = 0; i < scopes.length; i += 1) {
        const scope = scopes[i];
        if (!scope) continue;
        const found =
          scope.querySelector('product-form form[action*="/cart/add"]') ||
          scope.querySelector('form[action*="/cart/add"]');
        if (found) {
          this._form = found;
          return found;
        }
      }

      this._form = null;
      return null;
    }

    get productInfo() {
      if (this._productInfo && this._productInfo.isConnected) return this._productInfo;

      const form = this.form;
      this._productInfo =
        (form && (form.closest('product-info') || form.closest('.product') || form.closest('section'))) ||
        document.querySelector('product-info') ||
        document.getElementById('MainProduct') ||
        null;

      return this._productInfo;
    }

    /* ---------------------------------------------------------------- setup */

    parseVariants() {
      const script = this.querySelector('[data-satc-variants]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    setupVisibilityObserver() {
      if (typeof IntersectionObserver === 'undefined') return;

      this.io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting) {
            this.hide();
            return;
          }
          // Only reveal once the form has been scrolled PAST (sits above the
          // viewport), never when it is still below the fold.
          const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
          if (entry.boundingClientRect.bottom <= rootTop) {
            this.show();
          } else {
            this.hide();
          }
        },
        { root: null, threshold: 0 }
      );

      this.retargetObserver();
    }

    /*
     * The observed node is inside the product form, which Dawn replaces on
     * variant change — so re-point the observer whenever the target changes.
     */
    retargetObserver() {
      if (!this.io) return;

      const form = this.form;
      const target = form ? form.querySelector('.product-form__buttons') || form : null;

      if (!target || target === this.ioTarget) return;

      if (this.ioTarget) this.io.unobserve(this.ioTarget);
      this.ioTarget = target;
      this.io.observe(target);
    }

    /*
     * Resolve Dawn's pubsub API.
     *
     * assets/pubsub.js declares `subscribe` as a function declaration, so in a
     * classic (non-module) script it IS attached to window.
     *
     * assets/constants.js declares PUB_SUB_EVENTS with `const`, which does NOT
     * create a window property — it only lives in the global lexical scope. So
     * window.PUB_SUB_EVENTS is undefined and it must be read as a bare
     * identifier inside a try/catch, with the literal from constants.js as the
     * fallback.
     */
    getDawnSubscribe() {
      if (typeof window.subscribe === 'function') return window.subscribe;
      try {
        // eslint-disable-next-line no-undef
        if (typeof subscribe === 'function') return subscribe;
      } catch (error) {
        /* not defined in this scope */
      }
      return null;
    }

    getVariantChangeEventName() {
      try {
        // eslint-disable-next-line no-undef
        if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          // eslint-disable-next-line no-undef
          return PUB_SUB_EVENTS.variantChange;
        }
      } catch (error) {
        /* not defined in this scope */
      }
      return 'variant-change'; // literal value from Dawn assets/constants.js
    }

    setupVariantWatcher() {
      const dawnSubscribe = this.getDawnSubscribe();
      const eventName = this.getVariantChangeEventName();

      if (dawnSubscribe) {
        // PRIMARY: Dawn v16 product-info.js publishes
        //   publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId, html, variant } })
        // every time a variant resolves, for every picker type and any number
        // of options. This is the authoritative signal.
        this.unsubscribeVariantChange = dawnSubscribe(eventName, this.onVariantChange);
        console.log('[sticky-atc] Subscribed to Dawn pubsub event:', eventName);
      } else {
        console.warn(
          '[sticky-atc] Dawn pubsub `subscribe` not found — falling back to DOM change events. ' +
            'Check that pubsub.js loads before this script.'
        );
      }

      // SECONDARY (always attached, not just a fallback): Dawn's pubsub event
      // only ever fires for Shopify's own variant pickers — it knows nothing
      // about Appstle's subscription widget. Appstle's "Subscribe & Save" /
      // "One-Time Purchase" controls are plain native
      // <input type="radio" name="selling_plan"> elements, so listening for
      // 'change' (fired when a radio's checked state is committed) and
      // 'input' (fired earlier, and by some third-party widgets that toggle
      // state programmatically) on the document, in the capture phase,
      // reliably catches every Appstle selection without needing a
      // MutationObserver — even if Appstle later replaces/re-renders the
      // widget markup, since the listener lives on `document`, not on the
      // (possibly-replaced) radio nodes themselves.
      document.addEventListener('change', this.onSync, true);
      document.addEventListener('input', this.onSync, true);
      console.log('[sticky-atc] Listening for change/input events (variant pickers + Appstle selling_plan radios)');
    }

    /* Dawn pubsub handler — receives the resolved variant object directly. */
    onVariantChange(event) {
      const variant = event && event.data ? event.data.variant : null;

      if (!variant) {
        // Dawn publishes with no variant when the chosen combination does not
        // exist (e.g. Color+Size pair that was never created).
        console.log('[sticky-atc] Variant changed: none (unavailable combination)');
        this.selectedVariantId = null;
        this.syncHiddenInput(null);
        this.render();
        return;
      }

      console.log('[sticky-atc] Variant changed:', variant.id);

      this.selectedVariantId = String(variant.id);
      this.syncHiddenInput(this.selectedVariantId);
      this.retargetObserver();
      this.render();
    }

    /* Keeps the sticky bar's own hidden variant input in sync. */
    syncHiddenInput(variantId) {
      if (!this.hiddenInput) return;
      this.hiddenInput.value = variantId || '';
    }

    onSync() {
      if (this.syncTimer) clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => {
        this.retargetObserver();
        this.render();
      }, 60);
    }

    /* ------------------------------------------------------------ visibility */

    show() {
      if (this.visible) return;
      this.visible = true;
      this.bar.classList.add('is-visible');
      this.bar.setAttribute('aria-hidden', 'false');
      this.button.removeAttribute('tabindex');
    }

    hide() {
      if (!this.visible) return;
      this.visible = false;
      this.bar.classList.remove('is-visible');
      this.bar.setAttribute('aria-hidden', 'true');
      this.button.setAttribute('tabindex', '-1');
      this.hideError();
    }

    /* ---------------------------------------------------------------- state */

    findVariantById(id) {
      if (!id) return null;
      return this.variants.find((variant) => String(variant.id) === String(id)) || null;
    }

    /* Source 1: the live hidden input in the native product form. */
    variantFromForm() {
      const form = this.form;
      if (!form) return null;
      const input = form.querySelector('input[name="id"], select[name="id"]');
      if (!input || !input.value) return null;
      return this.findVariantById(input.value);
    }

    /* Source 2: the ?variant= parameter Dawn writes on every variant change. */
    variantFromUrl() {
      try {
        const id = new URLSearchParams(window.location.search).get('variant');
        return this.findVariantById(id);
      } catch (error) {
        return null;
      }
    }

    /*
     * Source 3: match the currently selected option values against the variant
     * option arrays. Independent of Dawn internals and handles any number of
     * options (Color only, Size only, or several at once).
     */
    selectedOptionValues() {
      const scope = this.productInfo;
      if (!scope) return [];

      const values = [];
      const groups = scope.querySelectorAll(
        'variant-selects .product-form__input, variant-radios .product-form__input, .product-form__input'
      );

      groups.forEach((group) => {
        // One control per option group, in DOM order = option order.
        const radio = group.querySelector('input[type="radio"]:checked');
        if (radio) {
          values.push(radio.value.trim());
          return;
        }
        const select = group.querySelector('select');
        if (select && select.value) values.push(select.value.trim());
      });

      if (values.length) return values;

      // Fallback for markup without .product-form__input wrappers.
      scope.querySelectorAll('variant-selects select, variant-radios select').forEach((select) => {
        if (select.value) values.push(select.value.trim());
      });
      scope
        .querySelectorAll('variant-selects input[type="radio"]:checked, variant-radios input[type="radio"]:checked')
        .forEach((radio) => values.push(radio.value.trim()));

      return values;
    }

    variantFromOptions() {
      const values = this.selectedOptionValues();
      if (!values.length) return null;

      const normalize = (list) => list.map((value) => String(value).trim().toLowerCase());
      const selected = normalize(values);

      // Exact positional match first (correct for well-formed Dawn markup).
      const exact = this.variants.find((variant) => {
        if (!variant.options || variant.options.length !== selected.length) return false;
        return normalize(variant.options).join('~') === selected.join('~');
      });
      if (exact) return exact;

      // Then "every option value is selected", which tolerates duplicate or
      // extra controls in the DOM.
      return (
        this.variants.find((variant) => {
          if (!variant.options || !variant.options.length) return false;
          return normalize(variant.options).every((option) => selected.indexOf(option) !== -1);
        }) || null
      );
    }

    currentVariant() {
      // Priority:
      //   1. id from Dawn's variantChange pubsub event (authoritative)
      //   2. live hidden input in the native product form
      //   3. ?variant= URL parameter
      //   4. selected option values
      //   5. the id rendered by Liquid on first paint
      return (
        this.findVariantById(this.selectedVariantId) ||
        this.variantFromForm() ||
        this.variantFromUrl() ||
        this.variantFromOptions() ||
        this.findVariantById(this.dataset.currentVariant) ||
        this.variants[0] ||
        null
      );
    }

    currentPlanId() {
      if (!this.form) return null;

      // Appstle renders a radio GROUP named "selling_plan": one radio per
      // subscription plan, plus a "One-Time Purchase" radio whose value is
      // intentionally "" (no plan). Whichever radio is :checked is
      // authoritative — including when its value is empty, which means the
      // shopper explicitly chose one-time purchase and NO selling_plan
      // should be sent. We must not fall through to another selector in
      // that case (that was the bug: it kept grabbing the first, unchecked,
      // subscription radio in DOM order instead of respecting "none").
      const checkedRadio = this.form.querySelector('input[name="selling_plan"]:checked');
      if (checkedRadio) {
        return checkedRadio.value ? String(checkedRadio.value) : null;
      }

      // No radio group present at all (e.g. a plain <select> widget) —
      // only then do we consult other selling_plan controls.
      const select = this.form.querySelector('select[name="selling_plan"]');
      if (select) return select.value ? String(select.value) : null;

      const hidden = this.form.querySelector('input[name="selling_plan"]:not([type="radio"])');
      if (hidden && hidden.value) return String(hidden.value);

      return null;
    }

    currentAllocation(variant) {
      const planId = this.currentPlanId();
      if (!planId || !variant || !variant.selling_plan_allocations) return null;
      return variant.selling_plan_allocations.find(
        (allocation) => String(allocation.selling_plan_id) === planId
      ) || null;
    }

    /* --------------------------------------------------------------- render */

    render() {
      const variant = this.currentVariant();

      if (!variant) {
        this.setText(this.priceEl, '');
        this.setDisabled(true, this.dataset.unavailableText || this.dataset.soldOutText);
        this.syncHiddenInput(null);
        console.log('[sticky-atc] Sticky variant updated: unavailable');
        return;
      }

      // Keep the sticky bar's hidden variant input authoritative.
      this.syncHiddenInput(variant.id);
      console.log('[sticky-atc] Sticky variant updated:', variant.id, '| available:', variant.available);

      // Detect + log selling_plan transitions distinctly (subscribe <-> one-time),
      // independent of whether the variant itself changed this render pass.
      const planId = this.currentPlanId();
      if (planId !== this.selectedPlanId) {
        console.log('[sticky-atc] Selling plan changed:', planId || 'none (one-time purchase)');
        this.selectedPlanId = planId;
      }

      const allocation = this.currentAllocation(variant);
      const price = allocation ? allocation.price : variant.price;
      const compare = allocation ? allocation.compare_at_price : variant.compare_at_price;

      this.setText(this.priceEl, price || '');

      if (this.compareEl) {
        if (compare) {
          this.setText(this.compareEl, compare);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }

      if (this.variantEl) {
        const showVariant = variant.title && variant.title !== 'Default Title';
        if (showVariant) {
          this.setText(this.variantEl, variant.title);
          this.variantEl.hidden = false;
          if (this.variantSepEl) this.variantSepEl.hidden = false;
        } else {
          this.variantEl.hidden = true;
          if (this.variantSepEl) this.variantSepEl.hidden = true;
        }
      }

      if (this.planEl) {
        if (allocation && allocation.name) {
          this.setText(this.planEl, allocation.name);
          this.planEl.hidden = false;
          if (this.planSepEl) this.planSepEl.hidden = false;
        } else {
          this.planEl.hidden = true;
          if (this.planSepEl) this.planSepEl.hidden = true;
        }
      }

      if (this.imageEl && variant.featured_image) {
        if (this.imageEl.getAttribute('src') !== variant.featured_image) {
          this.imageEl.setAttribute('src', variant.featured_image);
        }
      }

      this.renderStock(variant);

      if (!variant.available) {
        this.setDisabled(true, this.dataset.soldOutText);
      } else {
        this.setDisabled(false, this.dataset.buttonText);
      }

      console.log(
        '[sticky-atc] Sticky ATC updated successfully — variant:',
        variant.id,
        '| selling_plan:',
        planId || 'none (one-time)'
      );
    }

    renderStock(variant) {
      if (!this.stockEl) return;

      const tracked = variant.inventory_management === 'shopify';
      const denies = variant.inventory_policy === 'deny';
      const qty = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : 0;
      const isLow =
        variant.available &&
        tracked &&
        denies &&
        this.lowStockThreshold > 0 &&
        qty > 0 &&
        qty <= this.lowStockThreshold;

      if (isLow) {
        const template = this.dataset.lowStockText || 'Only [count] left';
        const message = template
          .replace(/\[count\]/g, qty)
          .replace(/\{\{\s*count\s*\}\}/g, qty);
        this.setText(this.stockEl, message);
        this.stockEl.hidden = false;
        if (this.stockSepEl) this.stockSepEl.hidden = false;
      } else {
        this.stockEl.hidden = true;
        if (this.stockSepEl) this.stockSepEl.hidden = true;
      }
    }

    setText(element, value) {
      if (!element) return;
      if (element.textContent !== value) element.textContent = value;
    }

    setDisabled(disabled, label) {
      if (disabled) {
        this.button.setAttribute('disabled', 'disabled');
      } else {
        this.button.removeAttribute('disabled');
      }
      if (label) this.setText(this.buttonLabel, label);
    }

    setLoading(state) {
      this.loading = state;
      this.bar.classList.toggle('is-loading', state);
      this.button.setAttribute('aria-busy', state ? 'true' : 'false');
      if (state) {
        this.button.setAttribute('disabled', 'disabled');
      } else {
        const variant = this.currentVariant();
        if (variant && variant.available) this.button.removeAttribute('disabled');
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.dataset.errorText || '';
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
    }

    /* ------------------------------------------------------------- add to cart */

    async onSubmit(event) {
      event.preventDefault();
      if (this.loading) return;

      const variant = this.currentVariant();

      if (!variant || !variant.available) {
        this.showError(this.dataset.soldOutText);
        return;
      }

      // No native form on the page (e.g. card context) — send them to the product.
      if (!this.form) {
        if (this.dataset.productUrl) window.location.href = this.dataset.productUrl;
        return;
      }

      this.hideError();
      this.setLoading(true);

      // Built from the LIVE native product form, so line item properties,
      // quantity and selling plan selections all carry over untouched.
      const form = this.form;
      const formData = new FormData(form);

      // Authoritative: the resolved variant always wins over whatever the form
      // happens to hold, so a mid-render form can never add the wrong variant.
      formData.set('id', variant.id);
      if (!formData.get('quantity')) formData.set('quantity', 1);

      // Authoritative selling_plan handling: FormData(form) already copied
      // whatever the native form's radio held (which, for "One-Time
      // Purchase", is name="selling_plan" value=""). An empty string is
      // still a present key, and Shopify's /cart/add treats a present
      // selling_plan differently from an absent one, so it must be
      // deleted outright rather than just left as "".
      const planId = this.currentPlanId();
      if (planId) {
        formData.set('selling_plan', planId);
      } else {
        formData.delete('selling_plan');
      }
      console.log('[sticky-atc] Submitting add-to-cart with variant:', variant.id, '| selling_plan:', planId || 'none (one-time)');

      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const container = cartDrawer || cartNotification;

      if (container && typeof container.getSectionsToRender === 'function') {
        const sections = container
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',');
        formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);
      }

      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        const data = await response.json();

        if (data.status) {
          this.showError(data.description || data.message || this.dataset.errorText);
          return;
        }

        if (container && typeof container.renderContents === 'function') {
          if (!cartDrawer && cartNotification && typeof cartNotification.setActiveElement === 'function') {
            cartNotification.setActiveElement(document.activeElement);
          }
          container.renderContents(data);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (error) {
        this.showError(this.dataset.errorText);
      } finally {
        this.setLoading(false);
      }
    }
  }

  customElements.define('sticky-atc', StickyAtc);
}
